<i18n>
{
	"en": {
		"header": "Preferred Vendors",
		"noPreferredVendorsFound": "No preferred vendors found...",
		"description": "Description",
		"startPostalRange": "From Postal Code",
		"endPostalRange": "To Postal Code",
		"editPreferredVendor": "Edit Preferred Vendor",
		"vendors": "Preferred Vendors",
		"deletePreferredVendor": "Delete Preferred Vendor",
		"newPreferredVendor": "New Preferred Vendor",
		"failedToFetch": "Failed to fetch preferred vendors",
		"failedToDelete": "Failed to delete preferred vendor",
		"failedToUpdatePostalCode": "Failed to update preferred vendor postal code",
		"failedToUpdateDescription": "Failed to update preferred vendor description",
		"updatedPreferredVendor": "Preferred Vendor Updated",
		"deletedPreferredVendors": "Deleted preferred vendor | Deleted preferred vendor | Deleted {count} preferred vendors"
	},
	"no": {
		"header": "Foretrukne Leverandører",
		"noPreferredVendorsFound": "Ingen foretrukne leverandører...",
		"description": "Beskrivelse",
		"startPostalRange": "Fra postnr.",
		"endPostalRange": "Til postnr.",
		"editPreferredVendor": "Rediger foretrukket leverandør",
		"vendors": "Foretrukne Leverandører",
		"deletePreferredVendor": "Fjern foretrukket leverandør",
		"newPreferredVendor": "Legg til foretrukket leverandør",
		"failedToFetch": "Kunne ikke hente foretrukne leverandører",
		"failedToUpdatePostalCode": "Kunne ikke oppdatere foretrukket leverandør postnummer",
		"failedToUpdateDescription": "Kunne ikke oppdatere foretrukket leverandør beskrivelse",
		"updatedPreferredVendor": "Foretrukket leverandør oppdatert",
		"deletedPreferredVendors": "Foretrukket leverandør ble slettet | Foretrukket leverandør ble slettet | Slettet {count} foretrukne leverandører"
	}
}
</i18n>

<template>
	<t-vertical-panel class="preferredVendors">
		<div slot="header" class="preferredVendors-header">
			<t-panel-header size="md" icon="new-link" type="bp">
				{{ $t('header') }}
			</t-panel-header>
		</div>
		<div slot="sub-header" class="preferredVendors-sub-header">
			<t-button
				:disabled="rowsSelected !== 1"
				iconBefore="annotation"
				appearance="minimal"
				@click="editPreferredVendor()"
			>{{ $t('editPreferredVendor') }}</t-button>
			<t-button
				:disabled="rowsSelected == 0"
				iconBefore="trash"
				appearance="primary"
				intent="danger"
				width="200px"
				type="bp"
				@click="onDeletePreferredVendors"
			>{{$tc('deletePreferredVendor', rowsSelected)}}</t-button>
			<t-button
				appearance="primary"
				width="200px"
				@click="onAddPreferredVendor"
			>{{$t('newPreferredVendor')}}</t-button>
		</div>
		<div class="preferredVendors-content">
			<t-grid
				v-if="preferredVendors"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinitions"
				:rowHeight="40"
				:items="preferredVendors"
				:selectableMode="['checkbox']"
				localStorageId="preferredVendorsList"
				selectable
				@selected="onRowsSelected"
				@rowDoubleClick="editPreferredVendor"
				@enter="editPreferredVendor()"
			>
				<template #description="{ item }">
					<t-text-input
						:value="item.description"
						:isInvalid="errors.description && item.id === errors.id"
						@change="updatePreferredVendor( item, $event, 'description' )"
					/>
				</template>
				<template #startPostalRange="{ item }">
					<t-text-input
						:value="item.startPostalRange"
						:isInvalid="errors.startPostalRange && item.id === errors.id"
						@change="updatePreferredVendor( item, $event, 'startPostalRange' )"
					/>
				</template>
				<template #endPostalRange="{ item }">
					<t-text-input
						:value="item.endPostalRange"
						:isInvalid="errors.endPostalRange && item.id === errors.id"
						@change="updatePreferredVendor( item, $event, 'endPostalRange' )"
					/>
				</template>
				<template #vendors="{ item, displayValue }">
					<t-tag-container
						:items="displayValue"
						displayKey="name"
						@change="removeTag(item, $event)"
					>
						<template v-slot="{item, index}">
							{{ index+1 }}. {{ item.name }}
						</template>
					</t-tag-container>
				</template>
				<template #options="{ item }">
					<t-icon-button
						:title="$t('editPreferredVendor')"
						icon="annotation"
						type="bp"
						appearance="minimal"
						@click="editPreferredVendor(item.id)"
					/>
				</template>
			</t-grid>
			<t-no-content-panel
				v-else
				:text="$t('noPreferredVendorsFound')"
				icon="book"
				iconType="bp"
			/>
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>
		<PreferredVendorsDialog
			:isShown="preferredVendorsShown"
			:preferredVendor="selectedPreferredVendor"
			:carrierVendors="carrierVendors"
			@ok="updatePreferredVendor( selectedPreferredVendor, $event, 'vendors' )"
			@close="preferredVendorsShown = false"
		/>
	</t-vertical-panel>
</template>

<script>
import { mapState, mapActions } from './store/preferredVendors.store.js';
import PreferredVendorsDialog from './PreferredVendorsDialog';

export default {
	name: 'PreferredVendors',
	components: { PreferredVendorsDialog },
	data() {
		return {
			defaultColumnDefinitions: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0,
			preferredVendorsShown: false,
			selectedPreferredVendor: null
		};
	},
	computed: {
		...mapState([
			'preferredVendors',
			'carrierVendors',
			'loading',
			'errors'
		]),
		columnDefinitions() {
			return [
				{
					field: 'description',
					displayName: this.$t('description'),
					minWidth: 100
				},
				{
					field: 'startPostalRange',
					displayName: this.$t('startPostalRange'),
					minWidth: 50
				},
				{
					field: 'endPostalRange',
					displayName: this.$t('endPostalRange'),
					minWidth: 50
				},
				{
					field: 'vendors',
					displayName: this.$t('vendors'),
					displayValueGetter: (item) => this.getFormattedVendors(item.vendors),
					minWidth: 200,
					sortable: false,
					type: 'array',
					comparableValueGetter: item => this.getVendorNames(item.vendors)
				},
				{
					field: 'options',
					displayName: '',
					sortable: false,
					resizable: false,
					width: 50
				}
			];
		}
	},
	async mounted() {
		try {
			await this.getPreferredVendors();
		} catch (e) {
			this.$notify({
				type: 'error',
				title: this.$t('failedToFetch')
			});
		}
	},
	methods:{
		...mapActions([
			'getPreferredVendors',
			'setPreferredVendor',
			'deletePreferredVendors',
			'addPreferredVendor',
		]),
		getVendorNames(vendors) {
			let ret = [];
			for (let v of vendors) {
				if (this.carrierVendors[v]) ret.push(this.carrierVendors[v].name);
			}
			return ret;
		},
		getFormattedVendors(vendors) {
			let ret = [];
			for (let v of vendors) {
				if (this.carrierVendors[v]) ret.push(this.carrierVendors[v]);
			}
			return ret;
		},
		removeTag(preferredVendor, newList) {
			this.updatePreferredVendor(preferredVendor, newList.map(x => x.id), 'vendors');
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		editPreferredVendor(id) {
			this.selectedPreferredVendor = undefined;
			if (id) {
				this.selectedPreferredVendor = this.preferredVendors.find( (x) => x.id === id );
			} else {
				let selectedItems = this.$refs.grid.getSelectedItems();
				if (selectedItems.length > 1) return;
				this.selectedPreferredVendor = selectedItems[0];
			}
			this.preferredVendorsShown = true;
		},
		async updatePreferredVendor(preferredVendor, data, key) {
			try {
				await this.setPreferredVendor({ preferredVendor: preferredVendor, data: data, prop: key });
				this.$notify({
					type: 'success',
					title: this.$t('updatedPreferredVendor'),
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdateCode'),
				});
			}
		},
		async onDeletePreferredVendors() {
			let selectedPreferredVendors = this.$refs.grid.getSelectedItems();
			if (!selectedPreferredVendors) return;
			try {
				await this.deletePreferredVendors(selectedPreferredVendors);
				this.$notify({
					type: 'success',
					title: this.$tc('deletedPreferredVendors', selectedPreferredVendors.length)
				});
				this.rowsSelected = 0;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToDelete')
				});
			}
		},
		async onAddPreferredVendor() {
			try {
				await this.addPreferredVendor();
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToAdd')
				});
			}
		},
	}
};

</script>

<style lang="scss" scoped>
.preferredVendors {
	.preferredVendors-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.preferredVendors-sub-header {
		display: flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: flex-end;

		> button {
			margin: 10px;
		}
	}
	.preferredVendors-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px;
	}
}
.list-item {
	display: flex;
	align-items: center;
	justify-items: flex-start;

	> div {
		margin-left: 10px;
	}
}
</style>
